import { useOne } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Col, Descriptions, Row, Space, Spin, Statistic, Table, Tag } from 'antd';
import type { TableProps } from 'antd';
import type { Company, Conference } from '@viq-admin/types';
import type { AggregationsResponse } from '@viq-admin/screens';
import {
  AsyncBoundary,
  EM_DASH,
  formatDate,
  formatDateTime,
  space,
  useNarrowViewport,
} from '@viq-admin/ui';
import { listConferenceCompanies, listConferenceUsers } from '../../api/conferences';
import type { UserWithAssignments } from '../../api/users';
import { superadminFetch } from '../../api/dataProvider';
import { CompanyLink, UserLink } from '../../components/EntityLinks';
import { LeadsMiniTable } from '../../components/LeadsMiniTable';
import { AutoEnrichPastLeads } from './AutoEnrichPastLeads';

/**
 * Overview tab for the conference show shell: the record's metadata, then
 * what the operator usually wants to know about an event without leaving
 * the page: how much has been captured, which tenants have access, which
 * reps are assigned, and the latest leads. Every name links to its record.
 * One write lives here too: "Auto enrichment for past leads"
 * (AutoEnrichPastLeads.tsx), the per-company manual start of the automatic run.
 *
 * The whole event, whatever "Operate as" says. Every read here is explicit
 * and cross-tenant:
 *  - the record: Refine `getOne` appends the global scope as `?companyId=`,
 *    which `GET /v1/superadmin/conferences/{id}` ignores, so the record is the
 *    same at any scope;
 *  - companies / users: `GET …/conferences/{id}/companies` and `…/users` take
 *    no company filter (superadminFetch never adds the scope);
 *  - capture counts: `GET …/conferences/{id}/aggregations` with no
 *    `companyId`, i.e. the cross-tenant branch;
 *  - recent leads: `LeadsMiniTable` with an explicit `{ conferenceId }`.
 *
 * Otherwise read-only. `read Conference` (record, companies), `read Assignment` (reps)
 * and `read Lead` (counts, leads) gate the backend routes; every console role
 * holds all three. Granting access and assigning reps stay on the Assignments
 * tab behind `manage:Conference`.
 *
 * The section queries share the `['conferenceOverview', id]` key prefix so
 * the Assignments tab can invalidate them after a write (it does, by that
 * literal prefix): antd keeps an already-visited tab mounted, so without it
 * this tab would show the old grants when the operator switches back.
 */

const conferenceOverviewKey = (conferenceId: string) => ['conferenceOverview', conferenceId] as const;

/**
 * Cross-tenant KPI counts for one conference (no `companyId`, so the backend
 * takes its all-tenant branch). Same endpoint the Batch screen reads; Batch
 * applies the "Operate as" scope, this deliberately does not.
 */
function fetchConferenceAggregations(conferenceId: string): Promise<AggregationsResponse> {
  return superadminFetch<AggregationsResponse>(
    `/v1/superadmin/conferences/${encodeURIComponent(conferenceId)}/aggregations`,
  );
}

/** Small stat tiles wrap on narrow screens instead of shrinking. */
const STAT_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))',
  gap: space.md,
};

export function ConferenceOverview({ conferenceId }: { conferenceId: string }) {
  const narrow = useNarrowViewport();
  const { result: conference, query } = useOne<Conference>({
    resource: 'conferences',
    id: conferenceId,
  });

  if (query.isLoading) return <Spin />;
  if (query.error) {
    return (
      <Alert
        type="error"
        message="Couldn't load conference"
        description={query.error.message}
      />
    );
  }
  if (!conference) return null;

  return (
    <Space direction="vertical" size={space.xl} style={{ width: '100%' }}>
      <Descriptions column={narrow ? 1 : 2} bordered size="small">
        <Descriptions.Item label="Name">{conference.name}</Descriptions.Item>
        <Descriptions.Item label="Location">
          {conference.location ?? EM_DASH}
        </Descriptions.Item>
        <Descriptions.Item label="Starts">
          {formatDate(conference.startDate)}
        </Descriptions.Item>
        <Descriptions.Item label="Ends">
          {formatDate(conference.endDate)}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {formatDateTime(conference.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Updated">
          {formatDateTime(conference.updatedAt)}
        </Descriptions.Item>
      </Descriptions>

      <CaptureCounts conferenceId={conferenceId} />

      <AccessAndReps conferenceId={conferenceId} />

      <AutoEnrichPastLeads conferenceId={conferenceId} />

      <Card size="small" title="Recent leads">
        <LeadsMiniTable filter={{ conferenceId }} hide={['conference']} />
      </Card>
    </Space>
  );
}

/**
 * Totals only. The aggregations response carries event-wide KPI and
 * coverage counts, no per-rep or per-company breakdown. Two of its fields
 * are left out on purpose: `meetings_booked` is always 0 (no schema field
 * behind it yet) and `unique_scans` repeats `total_scans`.
 */
function CaptureCounts({ conferenceId }: { conferenceId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...conferenceOverviewKey(conferenceId), 'aggregations'],
    queryFn: () => fetchConferenceAggregations(conferenceId),
  });

  const tiles: Array<{ key: string; title: string; hint: string; value: number | undefined }> = [
    { key: 'leads', title: 'Leads captured', hint: 'Leads at this event, all tenants (rep-deleted leads excluded)', value: data?.kpi.total_scans },
    { key: 'notes', title: 'Notes', hint: 'Notes on those leads', value: data?.kpi.total_notes },
    { key: 'tasks', title: 'Tasks', hint: 'Tasks on those leads', value: data?.kpi.total_tasks },
    { key: 'vip', title: 'VIPs', hint: 'Leads tagged VIP', value: data?.coverage.vip_count },
    { key: 'repeat', title: 'Repeat visitors', hint: 'Leads also captured at another event', value: data?.coverage.repeat_visitors },
    { key: 'employers', title: 'Contact companies', hint: "Distinct employers on the leads (the contacts' companies, not tenants)", value: data?.coverage.unique_companies },
  ];

  return (
    <section aria-label="Capture counts">
      <AsyncBoundary error={error} onRetry={() => void refetch()}>
        <div style={STAT_GRID}>
          {tiles.map((t) => (
            <Card key={t.key} size="small" loading={isLoading}>
              <Statistic title={<span title={t.hint}>{t.title}</span>} value={t.value ?? EM_DASH} />
            </Card>
          ))}
        </div>
      </AsyncBoundary>
    </section>
  );
}

/**
 * "Companies with access" beside "Reps assigned" on a wide screen, stacked on
 * a phone. Both come from the same two reads the Assignments tab makes; the
 * per-company rep count is derived from the reps list.
 */
function AccessAndReps({ conferenceId }: { conferenceId: string }) {
  const companies = useQuery({
    queryKey: [...conferenceOverviewKey(conferenceId), 'companies'],
    queryFn: () => listConferenceCompanies(conferenceId),
  });
  const reps = useQuery({
    queryKey: [...conferenceOverviewKey(conferenceId), 'users'],
    queryFn: () => listConferenceUsers(conferenceId),
  });

  const repCount = new Map<string, number>();
  for (const u of reps.data ?? []) repCount.set(u.companyId, (repCount.get(u.companyId) ?? 0) + 1);

  const companyColumns: NonNullable<TableProps<Company>['columns']> = [
    {
      key: 'name',
      title: 'Company',
      render: (_: unknown, c: Company) => (
        <Space size={space.xs} wrap>
          <CompanyLink id={c.id} name={c.name} />
          {c.archivedAt ? <Tag>Archived</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'reps',
      title: 'Reps assigned',
      align: 'right',
      width: 130,
      // Blank, not 0, until the reps list arrives: a 0 would be a claim.
      render: (_: unknown, c: Company) => (reps.data ? (repCount.get(c.id) ?? 0) : null),
    },
  ];

  const repColumns: NonNullable<TableProps<UserWithAssignments>['columns']> = [
    {
      key: 'rep',
      title: 'Rep',
      render: (_: unknown, u: UserWithAssignments) => (
        <Space size={space.xs} wrap>
          <UserLink oid={u.oid} name={u.name || u.email} />
          {u.deactivatedAt ? <Tag>Inactive</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'company',
      title: 'Company',
      render: (_: unknown, u: UserWithAssignments) => (
        <CompanyLink id={u.companyId} name={u.companyName} />
      ),
    },
  ];

  const sortedCompanies = [...(companies.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Row gutter={[space.lg, space.lg]}>
      <Col xs={24} lg={10}>
        <Card
          size="small"
          title={`Companies with access${companies.data ? ` (${companies.data.length})` : ''}`}
        >
          <AsyncBoundary error={companies.error} onRetry={() => void companies.refetch()}>
            <Table<Company>
              rowKey="id"
              size="small"
              dataSource={sortedCompanies}
              columns={companyColumns}
              loading={companies.isLoading}
              pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No company has access yet' }}
            />
          </AsyncBoundary>
        </Card>
      </Col>
      <Col xs={24} lg={14}>
        <Card size="small" title={`Reps assigned${reps.data ? ` (${reps.data.length})` : ''}`}>
          <AsyncBoundary error={reps.error} onRetry={() => void reps.refetch()}>
            <Table<UserWithAssignments>
              rowKey="oid"
              size="small"
              dataSource={reps.data ?? []}
              columns={repColumns}
              loading={reps.isLoading}
              pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No reps assigned yet' }}
            />
          </AsyncBoundary>
        </Card>
      </Col>
    </Row>
  );
}
